import React, { useState, useEffect } from "react";
import { DEFAULT_LIFECYCLE_SCRIPT, LifeCycleScript } from "../LifeCycleScript/index";
import { ContainerSelected } from "components/Environment/shared/BuildTypes";
import { Switch } from "components/Graphs/Switch";
import { KubernetesDashboardInfoTooltip } from "components/Environment/shared/KubernetesDashboardInfoTooltip";

interface KubernetesModeProps {
  selectedContainer: ContainerSelected;
  setSelectedContainer: (container: ContainerSelected) => void;
  localLifecycleScriptTab: "upload" | "paste";
  setLocalLifecycleScriptTab: (tab: "upload" | "paste") => void;
  localLifecycleScriptFile: File | null;
  setLocalLifecycleScriptFile: (file: File | null) => void;
  saveDescription?: string;
  onScriptValidationChange?: (isValid: boolean) => void;
}

export const KubernetesMode: React.FC<KubernetesModeProps> = ({
  selectedContainer,
  setSelectedContainer,
  localLifecycleScriptTab,
  setLocalLifecycleScriptTab,
  localLifecycleScriptFile,
  setLocalLifecycleScriptFile,
  saveDescription = "",
  onScriptValidationChange,
}) => {
  const [lifeCycleScript, setLifeCycleScript] = useState<string>(
    selectedContainer.vmBuild?.lifeCycleScriptAttr?.script || "",
  );
  const [k8sDashboard, setK8sDashboard] = useState<boolean>(
    selectedContainer.vmBuild?.k8s?.isDashboardEnabled ?? true,
  );
  const [isScriptEnabled, setIsScriptEnabled] = useState<boolean>(
    !!selectedContainer.vmBuild?.lifeCycleScriptAttr?.script,
  );

  // Update state when the container changes (e.g., when editing a launchable)
  useEffect(() => {
    setK8sDashboard(selectedContainer.vmBuild?.k8s?.isDashboardEnabled ?? true);
    const script = selectedContainer.vmBuild?.lifeCycleScriptAttr?.script || "";
    setLifeCycleScript(script);
    if (script) setIsScriptEnabled(true);
  }, [
    selectedContainer.vmBuild?.k8s?.isDashboardEnabled,
    selectedContainer.vmBuild?.lifeCycleScriptAttr?.script,
  ]);

  // Initialize k8s settings if not already set
  useEffect(() => {
    if (!selectedContainer.vmBuild?.k8s) {
      setSelectedContainer({
        vmBuild: {
          forceJupyterInstall: false,
          k8s: {
            isDisabled: false,
            isDashboardEnabled: k8sDashboard,
          },
          lifeCycleScriptAttr:
            isScriptEnabled && lifeCycleScript
              ? {
                  ...selectedContainer.vmBuild?.lifeCycleScriptAttr,
                  script: lifeCycleScript,
                }
              : undefined,
        },
      });
    }
  }, []);

  const handleK8sDashboardChange = (checked: boolean) => {
    setK8sDashboard(checked);
    setSelectedContainer({
      vmBuild: {
        forceJupyterInstall: false,
        k8s: {
          isDisabled: false,
          isDashboardEnabled: checked,
        },
        lifeCycleScriptAttr:
          isScriptEnabled && lifeCycleScript
            ? {
                ...selectedContainer.vmBuild?.lifeCycleScriptAttr,
                script: lifeCycleScript,
              }
            : undefined,
      },
    });
  };

  const handleScriptEnabledChange = (checked: boolean) => {
    setIsScriptEnabled(checked);
    if (!checked) {
      setSelectedContainer({
        vmBuild: {
          forceJupyterInstall: false,
          k8s: {
            isDisabled: false,
            isDashboardEnabled: k8sDashboard,
          },
          lifeCycleScriptAttr: undefined,
        },
      });
    } else {
      const script = lifeCycleScript || DEFAULT_LIFECYCLE_SCRIPT;
      setLifeCycleScript(script);
      setLocalLifecycleScriptTab("paste");
      setLocalLifecycleScriptFile(null);
      setSelectedContainer({
        vmBuild: {
          forceJupyterInstall: false,
          k8s: {
            isDisabled: false,
            isDashboardEnabled: k8sDashboard,
          },
          lifeCycleScriptAttr: {
            ...selectedContainer.vmBuild?.lifeCycleScriptAttr,
            script,
          },
        },
      });
    }
  };

  const handleLifeCycleScriptChange = (script: string) => {
    setLifeCycleScript(script);
    if (isScriptEnabled) {
      setSelectedContainer({
        vmBuild: {
          forceJupyterInstall: false,
          k8s: {
            isDisabled: false,
            isDashboardEnabled: k8sDashboard,
          },
          lifeCycleScriptAttr: script
            ? {
                ...selectedContainer.vmBuild?.lifeCycleScriptAttr,
                script: script,
              }
            : undefined,
        },
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="mb-6">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-medium text-white">
            Kubernetes dashboard
          </h3>
          <KubernetesDashboardInfoTooltip />
        </div>
        <div className="mt-3 flex flex-row items-center text-sm text-secondary">
          <Switch
            id="k8s-dashboard-enabled"
            checked={k8sDashboard}
            onCheckedChange={handleK8sDashboardChange}
            className="mr-2"
          />
          <label htmlFor="k8s-dashboard-enabled" className="text-primary">
            Install Kubernetes Dashboard
          </label>
        </div>
      </div>

      <div className="mb-6">
        <h3 className="text-base font-medium text-white">
          Cluster init script
        </h3>
        <p className="mt-1 text-sm text-secondary">
          This script runs automatically when your node starts. Use it to
          initialize your Kubernetes environment—install Helm charts, deploy
          manifests, or configure cluster components.
        </p>
        <div className="mt-3 flex flex-row items-center text-sm text-secondary">
          <Switch
            id="k8s-script-enabled"
            checked={isScriptEnabled}
            onCheckedChange={handleScriptEnabledChange}
            className="mr-2"
          />
          <label htmlFor="k8s-script-enabled" className="text-primary">
            Run a cluster init script
          </label>
        </div>
      </div>

      {/* Startup Script */}
      {isScriptEnabled && (
        <LifeCycleScript
          lifecycleScriptTab={localLifecycleScriptTab}
          setLifecycleScriptTab={setLocalLifecycleScriptTab}
          lifecycleScriptFile={localLifecycleScriptFile}
          setLifecycleScriptFile={setLocalLifecycleScriptFile}
          script={lifeCycleScript}
          onSetScript={handleLifeCycleScriptChange}
          saveDescription={saveDescription}
          experimentalFlag={false}
          hideTitle
          description=""
          placeholder={`#!/bin/bash\n\n# e.g. helm install ...`}
          onValidationChange={onScriptValidationChange}
        />
      )}
    </div>
  );
};
